import React, { useState, useEffect } from "react";
import "../AddAdvertisement.css";
import { isSpecialUser } from "../../../../../utils/environment";
import WeekdayOnlyDatePicker from "../components/WeekdayOnlyDatePicker";

const PlaceSavePage = ({
  formData,
  onInputChange,
  user,
  onNext,
  endSubmitTime,
  minStartDate,
  step = 1,
  keywordRanks = [],
  addKeywordRank,
  removeKeywordRank,
  updateKeywordRank,
  zoomSettings,
}) => {
  const [currentStep, setCurrentStep] = useState(step);
  const [urlError, setUrlError] = useState("");

  // step prop이 변경될 때 currentStep 업데이트
  useEffect(() => {
    setCurrentStep(step);
  }, [step]);

  // adStartDate와 adEndDate가 변경될 때 광고 기간 자동 계산
  useEffect(() => {
    if (formData.adStartDate && formData.adEndDate) {
      const startDate = new Date(formData.adStartDate);
      const endDate = new Date(formData.adEndDate);

      if (endDate >= startDate) {
        const timeDiff = endDate.getTime() - startDate.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // 시작일과 종료일 포함

        // console.log("📅 useEffect로 광고 기간 계산:", {
        //   startDate: formData.adStartDate,
        //   endDate: formData.adEndDate,
        //   timeDiff: timeDiff,
        //   daysDiff: daysDiff,
        //   explanation: `${formData.adStartDate}부터 ${formData.adEndDate}까지 ${daysDiff}일 (시작일과 종료일 모두 포함)`,
        // });

        onInputChange("adDuration", daysDiff.toString());
      }
    }
  }, [formData.adStartDate, formData.adEndDate]);

  // 내일 날짜 계산 (한국 시간 기준)
  const getTomorrowDate = () => {
    const now = new Date();
    const koreaTime = new Date(now.getTime() + 9 * 60 * 60 * 1000); // UTC+9 (한국 시간)
    const tomorrow = new Date(koreaTime);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  // 오늘 날짜 계산 (한국 시간 기준)
  const getTodayDate = () => {
    const now = new Date();
    const koreaTime = new Date(now.getTime() + 9 * 60 * 60 * 1000); // UTC+9 (한국 시간)
    return koreaTime.toISOString().split("T")[0];
  };

  // 최소 날짜 (내일, 또는 마감시각 이후면 모레 — 부모 minStartDate)
  const minDate = minStartDate || getTomorrowDate();
  const todayDate = getTodayDate();

  // 특정 유저는 오늘부터, 일반 유저는 마감시각 기준 최소일부터
  const userIsSpecial = isSpecialUser(user?.id);
  const effectiveMinDate = userIsSpecial ? todayDate : minDate;

  // 광고 종료일의 최소 날짜 (광고 시작일이 있으면 그 날짜, 없으면 최소 시작일)
  const getEndDateMin = () => {
    if (formData.adStartDate) {
      return formData.adStartDate;
    }
    return effectiveMinDate;
  };

  // 날짜 검증 함수
  const validateDate = (dateString) => {
    if (!dateString) return "날짜를 선택해주세요.";

    const selectedDate = new Date(dateString);
    const minAllowed = new Date(minDate);

    // 유효한 날짜인지 확인
    if (isNaN(selectedDate.getTime())) {
      return "올바른 날짜 형식이 아닙니다.";
    }

    // 특정 유저가 아닌 경우에만 최소 시작일 이후인지 확인
    if (!userIsSpecial && selectedDate < minAllowed) {
      return "광고 시작일은 주문 마감 기준 선택 가능한 날짜 이후여야 합니다.";
    }

    return "";
  };

  // 광고 시작일 입력 처리
  const handleAdStartDateChange = (e) => {
    const value = e.target.value;

    // 날짜 검증
    if (value) {
      const error = validateDate(value);
      if (error) {
        // 에러가 있으면 날짜를 초기화
        onInputChange("adStartDate", "");
        alert(error);
        return;
      }
      // 광고 시작일: 휴무로 설정된 토/일요일만 선택 불가 (VVIP 또는 특정 유저는 항상 가능)
      const selectedDate = new Date(value);
      const dayOfWeek = selectedDate.getDay();
      const allowWeekendByGrade = user?.price_grade?.name === "VVIP";
      const isSelectedDayOff =
        (dayOfWeek === 6 && !!zoomSettings?.end_submit_sat_off) ||
        (dayOfWeek === 0 && !!zoomSettings?.end_submit_sun_off);

      if (isSelectedDayOff && !allowWeekendByGrade && !userIsSpecial) {
        onInputChange("adStartDate", "");
        alert("이 날은 주문을 받지 않는 휴무일입니다. 다른 날짜를 선택해주세요.");
        return;
      }
    }

    // 광고 시작일 업데이트
    onInputChange("adStartDate", value);

    // 광고 시작일이 변경되면 모든 계산 재실행
    if (value) {
      // 진행 일수가 있으면 종료일을 다시 계산 (우선순위)
      const duration = parseInt(formData.adDuration) || 0;
      if (duration > 0) {
        const startDate = new Date(value);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + duration - 1); // 시작일 포함이므로 -1
        const endDateString = endDate.toISOString().split("T")[0];
        onInputChange("adEndDate", endDateString);
      } else if (formData.adEndDate) {
        // 종료일이 있으면 진행 일수를 다시 계산
        calculateAdDuration();
      }
    } else {
      // 광고 시작일이 비어있으면 종료일과 진행 일수 초기화
      onInputChange("adEndDate", "");
      onInputChange("adDuration", "");
    }
  };

  // 광고 종료일 입력 처리
  const handleAdEndDateChange = (e) => {
    const value = e.target.value;
    onInputChange("adEndDate", value);

    // 날짜 검증
    if (value) {
      const error = validateDate(value);
      if (error) {
        // 에러가 있으면 날짜를 초기화
        onInputChange("adEndDate", "");
        alert(error);
      }
    }

    // 광고 기간 자동 계산
    calculateAdDuration();
  };

  // 진행 일수 입력 처리
  const handleAdDurationChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, ""); // 숫자만 허용

    if (!formData.adStartDate) {
      alert("광고 시작일을 먼저 선택해주세요.");
      return;
    }

    if (value === "") {
      onInputChange("adDuration", "");
      onInputChange("adEndDate", "");
      return;
    }

    const duration = parseInt(value) || 0;

    if (duration <= 0) {
      alert("진행 일수는 1일 이상이어야 합니다.");
      return;
    }

    // 광고 시작일로부터 진행 일수만큼 더해서 종료일 계산
    const startDate = new Date(formData.adStartDate);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + duration - 1); // 시작일 포함이므로 -1

    const endDateString = endDate.toISOString().split("T")[0];

    onInputChange("adDuration", duration.toString());
    onInputChange("adEndDate", endDateString);
  };

  // 광고 기간 자동 계산 함수
  const calculateAdDuration = () => {
    if (formData.adStartDate && formData.adEndDate) {
      const startDate = new Date(formData.adStartDate);
      const endDate = new Date(formData.adEndDate);

      if (endDate >= startDate) {
        const timeDiff = endDate.getTime() - startDate.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // 시작일과 종료일 포함

        // console.log("📅 광고 기간 계산:", {
        //   startDate: formData.adStartDate,
        //   endDate: formData.adEndDate,
        //   timeDiff: timeDiff,
        //   daysDiff: daysDiff,
        //   explanation: `${formData.adStartDate}부터 ${formData.adEndDate}까지 ${daysDiff}일 (시작일과 종료일 모두 포함)`,
        // });

        onInputChange("adDuration", daysDiff.toString());
      }
    }
  };

  // 결제 금액 자동 계산 함수 (1회 단가 소수점 지원)
  const calculatePrice = () => {
    const dailyQuantity = parseInt(formData.dailyQuantity) || 0;
    const adDuration = parseInt(formData.adDuration) || 0;
    const userPriceByOne = parseFloat(formData.userPriceByOne) || 0;

    const totalQuantity = dailyQuantity * adDuration;
    const totalPrice = totalQuantity * userPriceByOne;

    onInputChange("totalQuantity", totalQuantity.toString());
    onInputChange("price", totalPrice.toString());
  };

  // 일일 수량, 광고 기간, 단가가 변경될 때마다 결제 금액 계산
  useEffect(() => {
    calculatePrice();
  }, [formData.dailyQuantity, formData.adDuration, formData.userPriceByOne]);

  // URL 포맷팅 함수
  const formatUrl = (url) => {
    if (!url) return "";

    // URL에서 쿼리 파라미터 제거
    let formattedUrl = url.split("?")[0];

    // 마지막 슬래시 제거
    formattedUrl = formattedUrl.replace(/\/$/, "");

    return formattedUrl;
  };

  // URL 검증 함수 (네이버 플레이스)
  const validateUrl = (url) => {
    if (!url) return "";

    // 일반적인 URL 패턴 검증
    const urlPattern = /^https?:\/\/[^\s/$.?#].[^\s]*$/;

    if (!urlPattern.test(url)) {
      return "올바른 URL 형식이 아닙니다.";
    }

    return "";
  };

  // URL 입력 처리
  const handleUrlChange = (e) => {
    const value = e.target.value;
    onInputChange("productUrl", value);
    setUrlError(""); // 입력 중에는 에러 메시지 제거
  };

  // URL 포커스 아웃 처리
  const handleUrlBlur = (e) => {
    const value = e.target.value;
    if (value) {
      const formattedUrl = formatUrl(value);
      const error = validateUrl(formattedUrl);

      if (error) {
        setUrlError(error);
      } else {
        onInputChange("productUrl", formattedUrl);
        setUrlError("");
      }
    }
  };

  const handleSubmit = () => {
    if (!user?.id) {
      onNext();
      return;
    }
    setCurrentStep(2);
  };

  const handleBackFromConfirmation = () => {
    setCurrentStep(1);
  };

  const handleConfirmOrder = () => {
    setCurrentStep(3);
  };

  // 키워드 입력 변경 핸들러 (입력 중에는 원본 유지, blur/Enter 시 trim + 중복 검사)
  const handleKeywordChange = (e, actualIndex) => {
    const inputValue = e.target.value;
    updateKeywordRank(actualIndex, "keyword", inputValue);
  };

  // 키워드 blur 핸들러 (trim + 중복 검사)
  const handleKeywordBlurWithValidation = (keyword, actualIndex) => {
    const trimmedKeyword = keyword.trim();

    if (keyword !== trimmedKeyword) {
      updateKeywordRank(actualIndex, "keyword", trimmedKeyword);
    }

    if (trimmedKeyword !== "") {
      const duplicateActualIndex = keywordRanks.findIndex(
        (item, idx) =>
          idx !== actualIndex &&
          item.keyword.trim() !== "" &&
          item.keyword.trim().toLowerCase() === trimmedKeyword.toLowerCase()
      );

      if (duplicateActualIndex !== -1) {
        const duplicateKeyword = keywordRanks[duplicateActualIndex];

        if (duplicateKeyword.selected) {
          alert("이미 등록된 키워드입니다.");
          removeKeywordRank(actualIndex);
          return;
        }

        updateKeywordRank(duplicateActualIndex, "selected", true);
        removeKeywordRank(actualIndex);
        return;
      }
    }
  };

  // 키워드 입력 필드 키 다운 처리 (엔터 키)
  const handleKeywordKeyDown = (e, keyword, actualIndex) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedKeyword = keyword.trim();

      if (keyword !== trimmedKeyword) {
        updateKeywordRank(actualIndex, "keyword", trimmedKeyword);
      }

      if (trimmedKeyword !== "") {
        const duplicateActualIndex = keywordRanks.findIndex(
          (item, idx) =>
            idx !== actualIndex &&
            item.keyword.trim() !== "" &&
            item.keyword.trim().toLowerCase() === trimmedKeyword.toLowerCase()
        );

        if (duplicateActualIndex !== -1) {
          const duplicateKeyword = keywordRanks[duplicateActualIndex];

          if (duplicateKeyword.selected) {
            alert("이미 등록된 키워드입니다.");
            removeKeywordRank(actualIndex);
            return;
          }

          updateKeywordRank(duplicateActualIndex, "selected", true);
          removeKeywordRank(actualIndex);
          return;
        }
      }

      e.target.blur();
    }
  };

  // 주문 확인 화면
  if (currentStep === 2) {
    return (
      <div className="shopping-visit-confirmation">
        {/* 빈 공간 */}
        <div className="spacer"></div>
        {/* 1. 상품 정보 입력 섹션 */}
        <div className="product-info-section">
          <div className="product-image-container">
            <div className="product-image">
              {formData.imageUrl ? (
                <img
                  src={formData.imageUrl}
                  alt="플레이스 이미지"
                  className="product-image-display"
                />
              ) : (
                <div className="image-placeholder">
                  <span>플레이스 이미지</span>
                </div>
              )}
            </div>
          </div>

          <div className="product-info-grid">
            {/* 첫 번째 줄
            <div className="info-row">
              <div className="form-field">
                <label>카테고리 상품 URL</label>
                <input type="text" value="" readOnly />
              </div>
              <div className="form-field">
                <label>카테고리 상품명</label>
                <input type="text" value="" readOnly />
              </div>
            </div> */}

            {/* 두 번째 줄 */}
            <div className="info-row">
              <div className="form-field">
                <label>플레이스명</label>
                <input
                  type="text"
                  value={formData.missionName || ""}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>플레이스 URL 주소</label>
                <input type="text" value={formData.productUrl || ""} readOnly />
              </div>
            </div>

            {/* 세 번째 줄 */}
            <div className="info-row">
              <div className="form-field">
                <label>플레이스 번호</label>
                <input type="text" value={formData.productId || ""} readOnly />
              </div>
              <div className="form-field">
                <label>메인키워드(순위체크용)</label>
                <input
                  type="text"
                  value={formData.mainKeyword?.name || ""}
                  readOnly
                />
              </div>
              {/* <div className="form-field">
                <label>상품번호</label>
                <input type="text" value="" readOnly />
              </div> */}
              <div className="form-field">
                <label>상품 이미지 주소</label>
                <input
                  type="text"
                  value={formData.imageUrl || ""}
                  placeholder="https://shopping..."
                  readOnly
                />
              </div>
            </div>

            {/* 네 번째 줄 */}
            <div className="info-row">
              <div className="form-field">
                <label>일일 수량</label>
                <input
                  type="text"
                  value={`${formData.dailyQuantity || 0}회`}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>광고 기간</label>
                <input
                  type="text"
                  value={`${formData.adDuration || 0}일`}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>광고 시작일</label>
                <input
                  type="text"
                  value={formData.adStartDate || ""}
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>

        {/* 2. 키워드 입력 섹션 */}
        <div className="keyword-section">
          <div className="add-kw-legend" aria-label="키워드 상태 안내">
            <span className="is-blue">정상</span>
            <span className="is-white">미입력</span>
          </div>
          <div className="keyword-boxes">
            {keywordRanks
              .filter((item) => item.selected)
              .map((item, index) => {
                const selectedKeywords = keywordRanks.filter((k) => k.selected);
                const actualIndex = keywordRanks.findIndex(
                  (keywordItem) =>
                    keywordItem.selected &&
                    selectedKeywords[index] === keywordItem
                );
                // 플레이스: 순위 UI 미표시 → 입력 여부만으로 톤(파랑/흰색)
                const hasKeyword = !!(item.keyword && item.keyword.trim());
                const boxTone = !hasKeyword
                  ? "empty"
                  : index === 0
                    ? "required"
                    : "optional";

                return (
                  <div
                    key={actualIndex}
                    className={`keyword-box ${boxTone}`}
                  >
                    <div className="keyword-content">
                      <div className="keyword-header is-place">
                        <button
                          type="button"
                          className="remove-keyword"
                          onClick={() => removeKeywordRank(actualIndex)}
                        >
                          ×
                        </button>
                      </div>
                      <span className="keyword-label">
                        {index === 0
                          ? `*유입용 키워드${index + 1}(필수)`
                          : `유입용 키워드${index + 1}(선택)`}
                      </span>
                      <input
                        type="text"
                        placeholder="유입용 키워드 입력"
                        value={item.keyword || ""}
                        onChange={(e) => handleKeywordChange(e, actualIndex)}
                        onBlur={(e) =>
                          handleKeywordBlurWithValidation(
                            e.target.value,
                            actualIndex
                          )
                        }
                        onKeyDown={(e) =>
                          handleKeywordKeyDown(e, e.target.value, actualIndex)
                        }
                        className="keyword-input-field"
                      />
                    </div>
                  </div>
                );
              })}

            {keywordRanks.filter((item) => item.selected).length < 10 && (
              <button
                type="button"
                className="add-keyword-btn"
                onClick={addKeywordRank}
              >
                +
              </button>
            )}
          </div>
        </div>

        {/* 3. 가격 및 수량 정보 섹션 */}
        <div className="pricing-section">
          <div className="pricing-grid">
            <div className="form-field">
              <label>1회 단가</label>
              <input
                type="text"
                value={`${(parseFloat(formData.userPriceByOne) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
                readOnly
              />
            </div>

            <div className="form-field">
              <label>신청 수량</label>
              <input
                type="text"
                value={`${formData.totalQuantity}회`}
                readOnly
              />
            </div>

            <div className="form-field">
              <label>결제 금액</label>
              <input
                type="text"
                value={`${(parseFloat(formData.price) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
                readOnly
              />
            </div>

            <div className="form-field">
              <label>광고 마감일(예정일)</label>
              <input
                type="text"
                value={
                  formData.endDate
                    ? formData.endDate.slice(2, 10).replace(/-/g, "-")
                    : ""
                }
                readOnly
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 완료 화면
  if (currentStep === 3) {
    return (
      <div className="advertisement-complete">
        <div className="complete-content">
          <div className="complete-icon">✅</div>
          <h2 className="complete-title">광고 등록이 완료되었습니다</h2>
          <p className="complete-description">
            등록하신 광고는 관리자 검토 후 승인됩니다.
            <br />
            승인 상태는 내 광고 확인에서 확인하실 수 있습니다.
          </p>
          <div className="complete-actions">
            <button
              className="complete-btn primary"
              onClick={() => onNext("myAdvertisement")}
            >
              내 광고 확인
            </button>
            <button
              className="complete-btn secondary"
              onClick={() => setCurrentStep(1)}
            >
              새 광고 등록
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="place-save-page">
      {/* <div className="page-description">
        <h3>네이버 플레이스 저장 광고</h3>
        <p>플레이스를 저장하여 저장 수를 높이는 광고 서비스입니다.</p>
      </div> */}

      <div className="add-form-fields">
        <section className="add-form-block add-form-block-basic">
          <h3 className="add-form-block-title">기본 정보</h3>
          {/* 첫 번째 줄: 플레이스 URL 주소 */}
          <div className="add-form-row">
            <div className="add-form-field">
              <label>플레이스 URL 주소</label>
              <input
                type="text"
                placeholder="https://map.naver.com/..."
                value={formData.productUrl}
                onChange={handleUrlChange}
                onBlur={handleUrlBlur}
                disabled={!user?.id}
                className={urlError ? "error" : ""}
              />
              {urlError && <p className="error-message">{urlError}</p>}
            </div>
          </div>

          {/* 두 번째 줄: 메인키워드(순위체크용) */}
          <div className="add-form-row">
            <div className="add-form-field">
              <label>메인키워드(순위체크용)</label>
              <input
                type="text"
                placeholder="필수입력"
                value={formData.mainKeyword?.name || ""}
                onChange={(e) => onInputChange("mainKeyword", e.target.value)}
                disabled={!user?.id}
                className="keyword-input"
              />
            </div>
          </div>
        </section>

        <section className="add-form-block add-form-block-schedule">
          <h3 className="add-form-block-title">수량 · 일정</h3>
          {/* 세 번째 줄: 일일 수량 */}
          <div className="add-form-row daily-quantity-row">
            <div className="add-form-field">
              <label>일일 수량</label>
              <input
                type="text"
                placeholder="100의 배수 입력"
                value={formData.dailyQuantity}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  onInputChange("dailyQuantity", value);
                }}
                disabled={!user?.id}
              />
            </div>
          </div>

          {/* 네 번째 줄: 광고 시작일, 진행 일수, 광고 종료일 */}
          <div className="add-form-row">
            <div className="add-form-field">
              <label>광고 시작일</label>
              <WeekdayOnlyDatePicker
                value={formData.adStartDate}
                onChange={(dateStr) =>
                  handleAdStartDateChange({ target: { value: dateStr } })
                }
                minDate={effectiveMinDate}
                disabled={!user?.id}
                userId={user?.id}
                blockSaturday={!!zoomSettings?.end_submit_sat_off}
                blockSunday={!!zoomSettings?.end_submit_sun_off}
              />
            </div>
            <div className="add-form-field">
              <label>진행 일수</label>
              <input
                type="text"
                placeholder="일수 입력"
                value={formData.adDuration || ""}
                onChange={handleAdDurationChange}
                disabled={!user?.id || !formData.adStartDate}
              />
            </div>
            <div className="add-form-field">
              <label>광고 종료일</label>
              <WeekdayOnlyDatePicker
                value={formData.adEndDate}
                onChange={(dateStr) =>
                  handleAdEndDateChange({ target: { value: dateStr } })
                }
                minDate={getEndDateMin()}
                disabled={!user?.id || !formData.adStartDate}
                weekdaysOnly={false}
                userId={user?.id}
              />
            </div>
          </div>
        </section>

        <section className="add-form-block add-form-block-summary">
          <h3 className="add-form-block-title">결제 요약</h3>
          {/* 다섯 번째 줄: 결제 금액 정보 */}
          <div className="add-form-row">
            <div className="add-form-field">
              <label>1회 단가</label>
              <input
                type="text"
                value={`${(parseFloat(formData.userPriceByOne) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
                readOnly
              />
            </div>
            <div className="add-form-field">
              <label>신청 수량</label>
              <input type="text" value={`${formData.totalQuantity}회`} readOnly />
            </div>
            <div className="add-form-field">
              <label>결제 금액</label>
              <input
                type="text"
                value={`${(parseFloat(formData.price) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
                readOnly
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PlaceSavePage;
